<template>
  <div class="article-page">
    <HeadNav selectedLanguage="en" page="blog" />

    <main>
      <article v-if="article">
        <header class="article-hero">
          <Breadcrumb :title="article.title" />
          <p class="article-kicker">Secure VPN Blog</p>
          <h1>{{ article.title }}</h1>
          <img :src="article.heroImage" :alt="article.heroAlt" width="960" height="540">
        </header>

        <section class="article-body">
          <template v-for="(block, blockIndex) in contentBlocks">
            <component
              :is="headingTag(block.level)"
              v-if="block.type === 'heading'"
              :id="block.id"
              :key="`heading-${blockIndex}`"
            >{{ block.title }}</component>

            <p v-else-if="block.type === 'paragraph'" :key="`paragraph-${blockIndex}`">
              <template v-for="(part, partIndex) in block.parts">
                <strong v-if="part.type === 'strong'" :key="`strong-${partIndex}`">{{ part.value }}</strong>
                <em v-else-if="part.type === 'em'" :key="`em-${partIndex}`">{{ part.value }}</em>
                <strong
                  v-else-if="part.type === 'link' && part.external && part.strong"
                  :key="`external-bold-link-${partIndex}`"
                >
                  <a
                    :href="part.href"
                    rel="nofollow"
                    target="_blank"
                  >{{ part.value }}</a>
                </strong>
                <a
                  v-else-if="part.type === 'link' && part.external"
                  :key="`external-link-${partIndex}`"
                  :href="part.href"
                  rel="nofollow"
                  target="_blank"
                >{{ part.value }}</a>
                <a
                  v-else-if="part.type === 'link'"
                  :key="`internal-link-${partIndex}`"
                  :href="part.href"
                >{{ part.value }}</a>
                <span v-else :key="`text-${partIndex}`">{{ part.value }}</span>
              </template>
            </p>

            <ul v-else-if="block.type === 'list'" :key="`list-${blockIndex}`">
              <li v-for="(item, itemIndex) in block.items" :key="`item-${itemIndex}`">
                <template v-for="(part, partIndex) in item">
                  <strong v-if="part.type === 'strong'" :key="`strong-${partIndex}`">{{ part.value }}</strong>
                  <em v-else-if="part.type === 'em'" :key="`em-${partIndex}`">{{ part.value }}</em>
                  <strong
                    v-else-if="part.type === 'link' && part.external && part.strong"
                    :key="`external-bold-link-${partIndex}`"
                  >
                    <a
                      :href="part.href"
                      rel="nofollow"
                      target="_blank"
                    >{{ part.value }}</a>
                  </strong>
                  <a
                    v-else-if="part.type === 'link' && part.external"
                    :key="`external-link-${partIndex}`"
                    :href="part.href"
                    rel="nofollow"
                    target="_blank"
                  >{{ part.value }}</a>
                  <a
                    v-else-if="part.type === 'link'"
                    :key="`internal-link-${partIndex}`"
                    :href="part.href"
                  >{{ part.value }}</a>
                  <span v-else :key="`text-${partIndex}`">{{ part.value }}</span>
                </template>
              </li>
            </ul>

            <div v-else-if="block.type === 'toc'" :key="`toc-${blockIndex}`" class="article-toc">
              <h2>Table of Contents</h2>
              <ul>
                <li
                  v-for="item in tocItems"
                  :key="item.id"
                  :class="`toc-level-${item.level}`"
                >
                  <a :href="`#${item.id}`">{{ item.title }}</a>
                </li>
              </ul>
            </div>

            <div v-else-if="block.type === 'table'" :key="`table-${blockIndex}`" class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th v-for="column in block.columns" :key="column">{{ column }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in block.rows" :key="`row-${rowIndex}`">
                    <td v-for="(cell, cellIndex) in row" :key="`cell-${cellIndex}`">
                      <span
                        v-if="block.columns[cellIndex] === 'Boxing Focus'"
                        class="boxing-stars"
                        :aria-label="cell"
                      >
                        <svg
                          v-for="star in 5"
                          :key="star"
                          class="boxing-star"
                          :class="{ 'is-filled': star <= boxingStarCount(cell) }"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M10 1.6l2.47 5 5.53.8-4 3.9.94 5.5L10 14.2l-4.94 2.6.94-5.5-4-3.9 5.53-.8z" />
                        </svg>
                      </span>
                      <template v-else>{{ cell }}</template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <figure v-else-if="block.type === 'image'" :key="`image-${blockIndex}`" class="article-image">
              <img :src="block.src" :alt="block.alt" width="960" height="540">
            </figure>
          </template>
        </section>
      </article>
    </main>
  </div>
</template>

<script>
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import { vpnBlogArticles } from '@/data/vpnBlogArticles.js';

export default {
  name: 'VpnBlogArticle',
  components: {
    HeadNav,
    Breadcrumb,
  },
  computed: {
    article() {
      return vpnBlogArticles[this.$route.meta.articleKey] || null;
    },
    contentBlocks() {
      if (!this.article) {
        return [];
      }
      return this.article.blocks.filter(
        (block, index) => !(index === 0 && block.type === 'image' && block.src === this.article.heroImage),
      );
    },
    tocItems() {
      return this.contentBlocks.filter(
        (block) => block.type === 'heading' && (block.level === 2 || block.level === 3),
      );
    },
  },
  methods: {
    headingTag(level) {
      return level === 3 ? 'h3' : 'h2';
    },
    boxingStarCount(value) {
      return (String(value).match(/\u2605/g) || []).length;
    },
  },
};
</script>

<style lang="scss" scoped>
.article-page {
  min-height: 70vh;
  background: #f7f5ff;
  color: var(--vpn-ink, #1f2430);
}

main {
  width: var(--vpn-content-width);
  max-width: 100%;
  margin: 0 auto;
  padding: 48px 0 72px;
  box-sizing: border-box;
}

article {
  overflow: hidden;
  border: 1px solid var(--vpn-line, #e8e4f8);
  border-radius: 8px;
  background: #fff;
  box-shadow: var(--vpn-shadow, 0 12px 40px rgba(109, 74, 255, 0.12));
}

.article-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 36px;
  background: #fbfcff;
  text-align: center;
}

.article-hero .breadcrumb {
  justify-content: center;
  margin-bottom: 26px;
}

.article-kicker {
  margin: 0 0 12px;
  color: #198754;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1 {
  margin: 0 auto 16px;
  color: var(--vpn-ink, #1f2430);
  font-size: 44px;
  line-height: 1.12;
  font-weight: 850;
}

.article-hero > img {
  display: block;
  width: min(720px, 100%);
  margin: 0 auto;
  height: auto;
  object-fit: contain;
}

.article-body {
  width: 100%;
  margin: 0 auto;
  padding: 38px 36px 48px;
  box-sizing: border-box;
}

h2 {
  margin: 34px 0 14px;
  color: var(--vpn-ink, #1f2430);
  font-size: 28px;
  line-height: 1.25;
}

h3 {
  margin: 24px 0 10px;
  color: var(--vpn-ink, #1f2430);
  font-size: 20px;
  line-height: 1.35;
}

p,
li {
  color: var(--vpn-ink-muted, #5b6475);
  font-size: 16px;
  line-height: 1.78;
}

p {
  margin: 0 0 14px;

  strong a {
    color: inherit;
    font-weight: 700;
  }
}

ul {
  margin: 0 0 18px;
  padding-left: 22px;
}

li + li {
  margin-top: 5px;
}

a {
  color: var(--vpn-brand, #6d4aff);
  font-weight: 700;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.article-toc {
  margin: 24px 0 30px;
  padding: 20px 22px;
  border: 1px solid #cde8d8;
  border-radius: 8px;
  background: #f2fbf6;
}

.article-toc h2 {
  margin: 0 0 12px;
  color: #137a4b;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 800;
}

.article-toc ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.article-toc li {
  line-height: 1.55;
}

.article-toc li + li {
  margin-top: 8px;
}

.article-toc .toc-level-2 > a {
  color: var(--vpn-ink, #1f2430);
  font-weight: 800;
}

.article-toc .toc-level-3 {
  margin-left: 18px;
  font-size: 15px;
}

.article-toc .toc-level-3 > a {
  color: var(--vpn-ink-muted, #5b6475);
  font-weight: 600;
}

.table-wrap {
  margin: 20px 0 28px;
  overflow-x: auto;
  border: 1px solid #dfe7f1;
  border-radius: 8px;
}

table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
}

th,
td {
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f5;
  text-align: left;
  vertical-align: top;
  font-size: 15px;
}

th {
  background: #f3f6fb;
  color: var(--vpn-ink, #1f2430);
  font-weight: 800;
}

td {
  color: var(--vpn-ink-muted, #5b6475);
}

.boxing-stars {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.boxing-star {
  width: 16px;
  height: 16px;
  fill: #d7dce5;
}

.boxing-star.is-filled {
  fill: #f5b400;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.article-image {
  margin: 28px 0;
  text-align: center;
}

.article-image img {
  display: block;
  width: min(720px, 100%);
  height: auto;
  margin: 0 auto;
  object-fit: contain;
}

@media (max-width: 768px) {
  main {
    width: 100%;
    padding: 28px 16px 48px;
    box-sizing: border-box;
  }

  .article-hero {
    padding: 24px;
  }

  h1 {
    font-size: 32px;
  }

  .article-body {
    padding: 28px 22px 38px;
  }

  h2 {
    font-size: 24px;
  }

  h3 {
    font-size: 19px;
  }

  p,
  li {
    font-size: 15px;
  }

  .article-toc {
    padding: 18px;
  }
}
</style>
